
import type { VoidFunctionComponent } from 'react';

import useGetReleaseGroupDependency, {
  getReleaseGroupDependencyQueryKey,
} from '../../../../hooks/api/dependencies/useGetReleaseGroupDependency';
import { queryClient } from '../../../../ReactQuery';

import DependencyDrawer from './DependencyDrawer';
import type { DrawerReleaseGroupScope } from './types';

type Props = {
  onClose: () => void;
  releaseGroupId: number;
  releaseGroupReleaseId: number;
  dependencyRevisionLocator: string;
  scope: DrawerReleaseGroupScope;
  /**
   * Used for Binary Dependencies. When provided, we preselect the chosen root project when loading
   * the components.
   */
  fromProjectRevisionLocator?: string;
  /**
   * A callback that's performed when a dependency is ignored or deleted.
   */
  onDeleteDependency?: () => void;
  /**
   * A callback that's performed when a dependency is edited.
   */
  onEditDependency?: () => void;
};

const ReleaseGroupDependencyDrawer: VoidFunctionComponent<Props> = ({
  onClose,
  releaseGroupId,
  releaseGroupReleaseId,
  dependencyRevisionLocator,
  scope,
  fromProjectRevisionLocator,
  onDeleteDependency,
  onEditDependency,
}) => {
  const { data, isLoading } = useGetReleaseGroupDependency(
    releaseGroupId,
    releaseGroupReleaseId,
    dependencyRevisionLocator,
  );

  // If we are loading from a binary dependency, we want to "preselect" the root project the binary component was
  // from. We do this by just moving it to the front of the array.
  if (fromProjectRevisionLocator && data && data?.dependency?.rootProjects?.length > 1) {
    const rootProjects = [...data.dependency.rootProjects];
    const matchingIndex = rootProjects.findIndex(
      (project) => project.revision === fromProjectRevisionLocator,
    );
    if (matchingIndex > 0) {
      const [matchingProject] = rootProjects.splice(matchingIndex, 1);
      if (matchingProject) {
        rootProjects.unshift(matchingProject);
      }
      data.dependency.rootProjects = rootProjects;
    }
  }

  return (
    <DependencyDrawer
      onClose={onClose}
      dependency={data?.dependency ?? null}
      isLoading={isLoading}
      onEditDependency={() => {
        queryClient.invalidateQueries({
          queryKey: getReleaseGroupDependencyQueryKey(releaseGroupId, releaseGroupReleaseId, dependencyRevisionLocator),
        });
        onEditDependency?.();
      }}
      onDeleteDependency={onDeleteDependency}
      scope={scope}
    />
  );
};

export default ReleaseGroupDependencyDrawer;
